import HCaptcha from '@hcaptcha/react-hcaptcha';
import { ErrorBoundary } from '@sentry/react';
import {
  HCAPTCHA_CN_API_HOST,
  HCAPTCHA_CN_ASSET_HOST,
  HCAPTCHA_CN_ENDPOINT,
  HCAPTCHA_CN_IMG_HOST,
  HCAPTCHA_CN_REPORT_API,
} from 'constants/captcha';
import { CHINA_COUNTRY_CODE } from 'constants/ui';
import {
  forwardRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { geolocation } from 'services/geolocation/geolocation';
import { captchaKey } from 'utils/captcha/captcha';
import { useCaptcha } from './useCaptcha';

const chinaHosts = {
  apihost: HCAPTCHA_CN_API_HOST,
  endpoint: HCAPTCHA_CN_ENDPOINT,
  assethost: HCAPTCHA_CN_ASSET_HOST,
  imghost: HCAPTCHA_CN_IMG_HOST,
  reportapi: HCAPTCHA_CN_REPORT_API,
};

const isChina = (countryCode?: string) =>
  countryCode?.toUpperCase() === CHINA_COUNTRY_CODE;

export type CaptchaProps = {
  onError?: () => void;
  onLoad?: () => void;
  onReady?: () => void;
  onVerify: (token: string) => Promise<void>;
  onClose?: () => void;
  'data-testid'?: string;
  children?: ReactNode;
};

export type CaptchaRef = {
  execute:
    | typeof HCaptcha.prototype.execute
    | ((opts?: Parameters<typeof HCaptcha.prototype.execute>[0]) => void);
  getToken: () => string;
  reset: () => void;
  isEnabled: () => boolean;
};

const Captcha = forwardRef<CaptchaRef, CaptchaProps>(
  (
    { onError, onLoad, onReady, onVerify, onClose, 'data-testid': testId },
    ref,
  ) => {
    const captchaRef = useRef<HCaptcha>(null);

    const {
      state: { isEnabled, token, isReady, countryCode },
      actions: { setReady, setCountryCode, setVerified },
    } = useCaptcha();

    const isCaptchaReady = captchaRef.current?.isReady();

    // biome-ignore lint/correctness/useExhaustiveDependencies: onReady could not be memoized
    useEffect(() => {
      if (isEnabled === undefined) {
        return;
      }

      const getCountryCode = async () => {
        try {
          const location = await geolocation.fetch();
          setCountryCode(location.countryCode || '');
        } catch {
          setCountryCode('');
        }
      };

      if (isEnabled) {
        getCountryCode();
      }

      if (!isReady && (isCaptchaReady || !isEnabled)) {
        onReady?.();
        setReady(true);
      }
    }, [isEnabled, isReady, isCaptchaReady]);

    const handleVerify = async (captchaToken: string) => {
      if (!captchaToken) {
        setVerified('');
        onError?.();
        return;
      }

      try {
        await onVerify(captchaToken);
        setVerified(captchaToken);
      } catch {
        setVerified('');
        onError?.();
      }
    };

    const shouldRender = !!isEnabled;

    useImperativeHandle(
      ref,
      () => ({
        execute: (opts: Parameters<typeof HCaptcha.prototype.execute>[0]) => {
          if (shouldRender) {
            return captchaRef.current?.execute(opts);
          }
        },
        reset: () => {
          if (shouldRender) {
            setVerified('');
            captchaRef.current?.resetCaptcha();
          }
        },

        getToken: () => {
          if (shouldRender) {
            return token;
          }

          return '';
        },
        isEnabled: () => {
          return shouldRender;
        },
      }),
      [shouldRender, token, setVerified],
    );

    if (!shouldRender) {
      return null;
    }

    const handleLoad = () => {
      onLoad?.();
    };

    const handleClose = () => {
      onClose?.();
    };

    const handleError = () => {
      onError?.();
    };

    const chinaProps = isChina(countryCode) ? { ...chinaHosts } : {};

    return (
      <ErrorBoundary fallback={<p>Something went wrong</p>}>
        <HCaptcha
          data-testid={testId}
          onLoad={handleLoad}
          onVerify={handleVerify}
          onClose={handleClose}
          onError={handleError}
          ref={captchaRef}
          sentry={false}
          sitekey={captchaKey}
          size="invisible"
          {...chinaProps}
        />
      </ErrorBoundary>
    );
  },
);

export { Captcha };
